import { defaultMarkdownPreviewStyle, mergeMarkdownPreviewStyle, type MarkdownPreviewStyle } from "./markdownPreviewStyle.js";
import { isShortcutCatalogCommandId } from "./shortcutCatalog.js";

const KEY = "npp-web-prefs";

export type WebPrefs = {
  defaultWordWrap: boolean;
  editorBaseFontPx: number;
  /** Markdown pane: line breaks, typography, column width (Settings → Markdown preview style…). */
  markdownPreviewStyle: MarkdownPreviewStyle;
  /** Only keys that differ from built-in defaults (see `shortcutCatalog.ts`). */
  shortcutOverrides?: Partial<Record<string, string>>;
  /**
   * When true, browser spellcheck + prose overlays run where allowed (preview-only, base64, etc. still gate).
   * Default is off; only an explicit `true` in storage enables spell checking.
   */
  nativeSpellcheckEnabled?: boolean;
  /** User dictionary: words never marked as misspelled in prose highlight. */
  spellcheckExceptions?: string[];
};

const defaults: WebPrefs = {
  defaultWordWrap: true,
  editorBaseFontPx: 14,
  markdownPreviewStyle: { ...defaultMarkdownPreviewStyle },
  nativeSpellcheckEnabled: false,
};

function sanitizeShortcutOverrides(raw: unknown): Partial<Record<string, string>> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const out: Partial<Record<string, string>> = {};
  for (const [k, v] of Object.entries(o)) {
    if (!isShortcutCatalogCommandId(k) || typeof v !== "string") continue;
    const s = v.trim();
    if (s) out[k] = s;
  }
  return Object.keys(out).length ? out : undefined;
}

function sanitizeSpellcheckExceptions(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const set = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const s = item.trim().toLowerCase();
    if (s.length < 2 || !/^[a-z']+$/.test(s)) continue;
    set.add(s);
  }
  return set.size ? [...set] : undefined;
}

export function loadWebPrefs(): WebPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...defaults };
    const o = JSON.parse(raw) as Partial<WebPrefs>;
    return {
      defaultWordWrap: typeof o.defaultWordWrap === "boolean" ? o.defaultWordWrap : defaults.defaultWordWrap,
      editorBaseFontPx:
        typeof o.editorBaseFontPx === "number" && o.editorBaseFontPx >= 10 && o.editorBaseFontPx <= 32
          ? o.editorBaseFontPx
          : defaults.editorBaseFontPx,
      markdownPreviewStyle: mergeMarkdownPreviewStyle(o.markdownPreviewStyle),
      shortcutOverrides: sanitizeShortcutOverrides(o.shortcutOverrides),
      nativeSpellcheckEnabled: o.nativeSpellcheckEnabled === true,
      spellcheckExceptions: sanitizeSpellcheckExceptions(o.spellcheckExceptions),
    };
  } catch {
    return { ...defaults };
  }
}

export function saveWebPrefs(p: WebPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}
